import React, { Component } from 'react';
import { visitSessionsRef } from '../config/firebase';
import { onValue } from 'firebase/database';
import PageLoader from './PageLoader';
import {
    LuChartColumn,
    LuEye,
    LuCalendarDays,
    LuGlobe,
    LuHistory,
    LuArrowUpRight,
    LuMonitor,
    LuSmartphone,
    LuTablet,
    LuX,
    LuUsers,
    LuMapPin
} from 'react-icons/lu';
import '../stylesheet/style.css';
import '../stylesheet/Analytics.css';

// Grouped in UTC throughout (both when bucketing real visits and when
// generating the last-14-days scaffold) so the two always agree on what
// "today" means - a small offset from the visitor's local midnight is a
// fine trade-off for not needing a timezone library.
const dayKey = (timestamp) => new Date(timestamp).toISOString().slice(0, 10);

const dayLabel = (dateStr) => {
    const d = new Date(`${dateStr}T00:00:00Z`);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const buildLastNDays = (visits, days = 14) => {
    const counts = {};
    visits.forEach((v) => {
        const key = dayKey(v.timestamp);
        counts[key] = (counts[key] || 0) + 1;
    });
    const result = [];
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date();
        d.setUTCHours(0, 0, 0, 0);
        d.setUTCDate(d.getUTCDate() - i);
        const key = d.toISOString().slice(0, 10);
        result.push({ date: key, label: dayLabel(key), count: counts[key] || 0 });
    }
    return result;
};

// Same day-buckets as buildLastNDays, but counts distinct devices per day
// instead of raw visits - filled in below once deviceGroupKey exists.
const buildLastNDaysUnique = (visits, keyFn, days = 14) => {
    const dayGroups = {};
    visits.forEach((v) => {
        const day = dayKey(v.timestamp);
        if (!dayGroups[day]) dayGroups[day] = new Set();
        dayGroups[day].add(keyFn(v));
    });
    const result = [];
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date();
        d.setUTCHours(0, 0, 0, 0);
        d.setUTCDate(d.getUTCDate() - i);
        const key = d.toISOString().slice(0, 10);
        result.push({ date: key, label: dayLabel(key), count: dayGroups[key] ? dayGroups[key].size : 0 });
    }
    return result;
};

const topPaths = (visits, limit = 5) => {
    const counts = {};
    visits.forEach((v) => {
        const key = v.path || '/';
        counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
        .map(([path, count]) => ({ path, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit);
};

const formatPath = (path) => {
    if (!path || path === '/') return 'Home';
    const clean = path.replace(/^\//, '');
    return clean.charAt(0).toUpperCase() + clean.slice(1);
};

// Visits now log the whole ipwho.is response under entry.geo. A handful
// of entries logged before that change still carry the older flat
// ip/city/region/country/countryCode shape directly on the entry - this
// normalizes both into the same { ip, city, region, country, country_code }
// shape so the rest of this file only ever deals with one format.
const getGeo = (entry) => {
    if (entry.geo) return entry.geo;
    if (entry.ip || entry.city || entry.country || entry.countryCode) {
        return {
            ip: entry.ip,
            city: entry.city,
            region: entry.region,
            country: entry.country,
            country_code: entry.countryCode,
        };
    }
    return null;
};

const formatLocation = (geo) => {
    if (!geo || (!geo.city && !geo.country)) return 'Unknown';
    if (geo.city && geo.country_code) return `${geo.city}, ${geo.country_code}`;
    return geo.city || geo.country || 'Unknown';
};

// ISO 3166-1 alpha-2 -> flag emoji, built from the two regional indicator
// symbols rather than shipping a flag-icon set for this one use.
const countryFlagEmoji = (code) => {
    if (!code || code.length !== 2) return '';
    const points = [...code.toUpperCase()].map((c) => 127397 + c.charCodeAt(0));
    return String.fromCodePoint(...points);
};

const DEVICE_ICONS = { Desktop: LuMonitor, Mobile: LuSmartphone, Tablet: LuTablet };

// Counts unique visitors (distinct IP+browser+OS+device fingerprints) per
// device type, not raw page views - one visitor loading 10 pages on their
// phone should count as 1 Mobile visitor, not 10.
const deviceBreakdown = (visits) => {
    const sets = {};
    visits.forEach((v) => {
        const type = (v.device && v.device.deviceType) || 'Unknown';
        if (!sets[type]) sets[type] = new Set();
        sets[type].add(deviceGroupKey(v));
    });
    return Object.entries(sets)
        .map(([type, set]) => ({ type, count: set.size }))
        .sort((a, b) => b.count - a.count);
};

// "Chrome 128 · Windows 10/11" - just the major version, patch numbers
// don't add much for a quick scan of the log.
const formatDeviceChip = (device) => {
    if (!device) return null;
    const parts = [];
    if (device.browser && device.browser !== 'Unknown') {
        const major = device.browserVersion ? device.browserVersion.split('.')[0] : '';
        parts.push(major ? `${device.browser} ${major}` : device.browser);
    }
    if (device.os && device.os !== 'Unknown') parts.push(device.os);
    return parts.join(' · ') || null;
};

// Same visitor loading three pages used to show up as three separate rows
// in the log. Grouping by IP + browser/OS/device collapses that into one
// row per real device, with every individual visit still available behind
// it (see the popup).
const deviceGroupKey = (entry) => {
    const geo = getGeo(entry);
    const device = entry.device || {};
    const ip = (geo && geo.ip) || 'unknown-ip';
    return `${ip}__${device.browser || 'unknown'}__${device.os || 'unknown'}__${device.deviceType || 'unknown'}`;
};

const groupVisitsByDevice = (visits, limit = 100) => {
    // Newest-first, so within each group entries[0] is the most recent
    // visit, and groups themselves come out ordered by their own most
    // recent visit (a Map keeps first-insertion order, and the first
    // visit pushed into a given group - while scanning newest-first - is
    // that group's most recent one).
    const sorted = visits.slice().sort((a, b) => b.timestamp - a.timestamp);
    const groups = new Map();
    sorted.forEach((entry) => {
        const key = deviceGroupKey(entry);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(entry);
    });
    return Array.from(groups.entries())
        .map(([key, entries]) => ({
            key,
            entries,
            count: entries.length,
            lastVisit: entries[0],
            geo: getGeo(entries[0]),
            device: entries[0].device,
        }))
        .slice(0, limit);
};

// Same idea - unique visitors per location, not raw page views, so one
// person browsing 5 pages from Chennai doesn't outweigh 3 separate
// visitors from 3 other cities.
const topLocations = (visits, limit = 5) => {
    const sets = {};
    visits.forEach((v) => {
        const key = formatLocation(getGeo(v));
        if (!sets[key]) sets[key] = new Set();
        sets[key].add(deviceGroupKey(v));
    });
    return Object.entries(sets)
        .map(([location, set]) => ({ location, count: set.size }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit);
};

// Full country-by-country breakdown (unique visitors, not raw page views)
// for the "Countries reached" stat card's popup.
const countryBreakdown = (visits) => {
    const sets = {};
    const codes = {};
    visits.forEach((v) => {
        const geo = getGeo(v);
        const country = (geo && geo.country) || null;
        if (!country) return;
        if (!sets[country]) { sets[country] = new Set(); codes[country] = geo.country_code; }
        sets[country].add(deviceGroupKey(v));
    });
    return Object.entries(sets)
        .map(([country, set]) => ({ country, countryCode: codes[country], count: set.size }))
        .sort((a, b) => b.count - a.count);
};

// Same, but per state/region for the "States reached" stat card's popup.
// Region names alone can collide across countries (there's more than one
// "Georgia"), so the grouping key includes the country code even though
// only the region name is shown.
const regionBreakdown = (visits) => {
    const sets = {};
    const labels = {};
    visits.forEach((v) => {
        const geo = getGeo(v);
        const region = geo && geo.region;
        if (!region) return;
        const key = geo.country_code ? `${region}__${geo.country_code}` : region;
        if (!sets[key]) {
            sets[key] = new Set();
            labels[key] = geo.country_code ? `${region}, ${geo.country_code}` : region;
        }
        sets[key].add(deviceGroupKey(v));
    });
    return Object.entries(sets)
        .map(([key, set]) => ({ key, label: labels[key], count: set.size }))
        .sort((a, b) => b.count - a.count);
};

export default class Analytics extends Component {

    constructor(props) {
        super(props);
        this.state = {
            visits: [],
            contentLoading: true,
            selectedGroupKey: null,
            modalClosing: false,
            statModalKey: null,
            statModalClosing: false,
        };
    }

    componentDidMount() {
        onValue(visitSessionsRef, (snapshot) => {
            const sessions = snapshot.val() || {};
            // Storage keeps geo/device once per session with page views
            // nested inside it (see actions/visitTracker.jsx) - flatten
            // that back into one row per page view here, so every chart/
            // grouping helper below can keep working exactly as before
            // against a flat visits array.
            const visits = [];
            Object.entries(sessions).forEach(([sessionId, session]) => {
                // Sessions logged while signed in as admin are still saved
                // (so the Firebase console shows tracking is actually
                // working), just excluded from the visitor-facing numbers
                // here - otherwise every admin page-check would count as a
                // "visit" and skew everything.
                if (session.isAdmin) return;
                const pageViews = session.pageViews
                    ? (Array.isArray(session.pageViews) ? session.pageViews : Object.values(session.pageViews))
                    : [];
                pageViews.forEach((pv) => {
                    if (!pv) return;
                    visits.push({
                        path: pv.path,
                        timestamp: pv.timestamp,
                        referrer: session.referrer,
                        geo: session.geo,
                        device: session.device,
                        sessionId,
                    });
                });
            });
            this.setState({ visits, contentLoading: false });
        });
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
        window.clearTimeout(this._modalTimer);
        window.clearTimeout(this._statModalTimer);
    }

    handleKeyDown = (event) => {
        if (event.key !== 'Escape') return;
        if (this.state.selectedGroupKey) this.closeGroupModal();
        if (this.state.statModalKey) this.closeStatModal();
    }

    openGroupModal = (key) => {
        this.setState({ selectedGroupKey: key, modalClosing: false });
    }

    openStatModal = (key) => {
        this.setState({ statModalKey: key, statModalClosing: false });
    }

    closeStatModal = () => {
        // Same 320ms exit-animation convention as the device-history popup.
        this.setState({ statModalClosing: true });
        window.clearTimeout(this._statModalTimer);
        this._statModalTimer = window.setTimeout(() => {
            this.setState({ statModalKey: null, statModalClosing: false });
        }, 320);
    }

    closeGroupModal = () => {
        // Same 320ms exit-animation convention used by every other modal
        // on this site (Achievements, Home profile/crop, ...).
        this.setState({ modalClosing: true });
        window.clearTimeout(this._modalTimer);
        this._modalTimer = window.setTimeout(() => {
            this.setState({ selectedGroupKey: null, modalClosing: false });
        }, 320);
    }

    render() {
        const { visits, contentLoading } = this.state;
        const total = visits.length;
        const todayKey = dayKey(Date.now());
        const todayCount = visits.filter((v) => dayKey(v.timestamp) === todayKey).length;
        const days = buildLastNDays(visits, 14);
        const top = topPaths(visits, 5);
        const topPage = top[0];
        const locations = topLocations(visits, 5);
        const devices = deviceBreakdown(visits);
        const regionCount = new Set(
            visits.map((v) => { const geo = getGeo(v); return geo && geo.region; }).filter(Boolean)
        ).size;
        const countryCount = new Set(
            visits.map((v) => { const geo = getGeo(v); return geo && geo.country_code; }).filter(Boolean)
        ).size;
        const countries = countryBreakdown(visits);
        const regions = regionBreakdown(visits);
        // Same IP + browser/OS/device fingerprint used to group the log
        // below - counts different people, not different page loads.
        const uniqueVisitorCount = new Set(visits.map(deviceGroupKey)).size;
        const uniqueDays = buildLastNDaysUnique(visits, deviceGroupKey, 14);
        const maxUniqueCount = Math.max(1, ...uniqueDays.map((d) => d.count));
        const maxCount = Math.max(1, ...days.map((d) => d.count));
        const groups = groupVisitsByDevice(visits, 100);
        const selectedGroup = this.state.selectedGroupKey
            ? groups.find((g) => g.key === this.state.selectedGroupKey)
            : null;
        const selectedGeo = selectedGroup ? selectedGroup.geo : null;
        const selectedDevice = selectedGroup ? selectedGroup.device : null;
        const SelectedDeviceIcon = (selectedDevice && DEVICE_ICONS[selectedDevice.deviceType]) || LuMonitor;

        const chartWidth = 700;
        const chartHeight = 220;
        const chartPaddingBottom = 34;
        const chartPaddingTop = 20;
        const barAreaHeight = chartHeight - chartPaddingBottom - chartPaddingTop;
        const barGap = 8;
        const barWidth = (chartWidth / days.length) - barGap;

        // Shared by both the total-visits and unique-visitors charts below
        // - same 14-bar geometry, just fed different data/max/wording.
        const renderChartSvg = (data, max, unit, ariaLabel) => (
            <svg
                className="analytics-chart"
                viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                role="img"
                aria-label={ariaLabel}
                preserveAspectRatio="none"
            >
                {data.map((d, i) => {
                    const rawH = (d.count / max) * barAreaHeight;
                    const h = d.count > 0 ? Math.max(rawH, 3) : 0;
                    const x = i * (barWidth + barGap) + barGap / 2;
                    const y = chartPaddingTop + (barAreaHeight - h);
                    return (
                        <g key={d.date}>
                            <title>{`${d.label}: ${d.count} ${unit}${d.count === 1 ? '' : 's'}`}</title>
                            <rect className="analytics-bar" x={x} y={y} width={barWidth} height={h} rx="4" />
                            {d.count > 0 && (
                                <text className="analytics-bar-value" x={x + barWidth / 2} y={y - 6} textAnchor="middle">{d.count}</text>
                            )}
                            <text className="analytics-bar-label" x={x + barWidth / 2} y={chartHeight - 12} textAnchor="middle">{d.label}</text>
                        </g>
                    );
                })}
            </svg>
        );

        const renderChartTable = (data, caption, unit) => (
            <table className="sr-only">
                <caption>{caption}</caption>
                <thead><tr><th>Date</th><th>{unit}s</th></tr></thead>
                <tbody>
                    {data.map((d) => (<tr key={d.date}><td>{d.label}</td><td>{d.count}</td></tr>))}
                </tbody>
            </table>
        );

        return (
            <div className="base">
                <div className="main-content-outer">
                    <h1 className="sr-only">Site Analytics</h1>
                    <span className="page-watermark" aria-hidden="true">Analytics</span>
                    {contentLoading && <PageLoader />}
                    {!contentLoading && (
                        <>
                        <div className="analytics-stats">
                            <div className="analytics-stat-card">
                                <span className="analytics-stat-icon"><LuEye /></span>
                                <span className="analytics-stat-value">{total}</span>
                                <span className="analytics-stat-label">Total visits</span>
                            </div>
                            <div className="analytics-stat-card">
                                <span className="analytics-stat-icon"><LuUsers /></span>
                                <span className="analytics-stat-value">{uniqueVisitorCount}</span>
                                <span className="analytics-stat-label">Unique visitors</span>
                            </div>
                            <div className="analytics-stat-card">
                                <span className="analytics-stat-icon"><LuCalendarDays /></span>
                                <span className="analytics-stat-value">{todayCount}</span>
                                <span className="analytics-stat-label">Visits today</span>
                            </div>
                            <div className="analytics-stat-card">
                                <span className="analytics-stat-icon"><LuArrowUpRight /></span>
                                <span className="analytics-stat-value analytics-stat-value-text">{topPage ? formatPath(topPage.path) : '—'}</span>
                                <span className="analytics-stat-label">Most visited page</span>
                            </div>
                            <div
                                className="analytics-stat-card analytics-stat-card-clickable"
                                onClick={() => this.openStatModal('states')}
                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), this.openStatModal('states'))}
                                role="button"
                                tabIndex={0}
                                aria-label="View states reached breakdown"
                            >
                                <span className="analytics-stat-icon"><LuMapPin /></span>
                                <span className="analytics-stat-value">{regionCount || '—'}</span>
                                <span className="analytics-stat-label">States reached</span>
                            </div>
                            <div
                                className="analytics-stat-card analytics-stat-card-clickable"
                                onClick={() => this.openStatModal('countries')}
                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), this.openStatModal('countries'))}
                                role="button"
                                tabIndex={0}
                                aria-label="View countries reached breakdown"
                            >
                                <span className="analytics-stat-icon"><LuGlobe /></span>
                                <span className="analytics-stat-value">{countryCount || '—'}</span>
                                <span className="analytics-stat-label">Countries reached</span>
                            </div>
                        </div>

                        <div className="analytics-charts-row">
                            <div className="analytics-card analytics-chart-card">
                                <h3 className="analytics-card-heading"><LuChartColumn /> Total visits - last 14 days</h3>
                                {total === 0 ? (
                                    <p className="analytics-empty">No visits logged yet - come back once real traffic starts arriving.</p>
                                ) : (
                                    <>
                                        {renderChartSvg(days, maxCount, 'visit', `Bar chart of visits over the last 14 days, from ${days[0].label} to ${days[days.length - 1].label}`)}
                                        {renderChartTable(days, 'Visits per day, last 14 days', 'Visit')}
                                    </>
                                )}
                            </div>

                            <div className="analytics-card analytics-chart-card">
                                <h3 className="analytics-card-heading"><LuUsers /> Unique visitors - last 14 days</h3>
                                {uniqueVisitorCount === 0 ? (
                                    <p className="analytics-empty">No visits logged yet - come back once real traffic starts arriving.</p>
                                ) : (
                                    <>
                                        {renderChartSvg(uniqueDays, maxUniqueCount, 'visitor', `Bar chart of unique visitors over the last 14 days, from ${uniqueDays[0].label} to ${uniqueDays[uniqueDays.length - 1].label}`)}
                                        {renderChartTable(uniqueDays, 'Unique visitors per day, last 14 days', 'Visitor')}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="analytics-two-col">
                            <div className="analytics-card">
                                <h3 className="analytics-card-heading"><LuArrowUpRight /> Top pages</h3>
                                {top.length === 0 ? (
                                    <p className="analytics-empty">No page data yet.</p>
                                ) : (
                                    <ul className="analytics-top-list">
                                        {top.map(({ path, count }) => {
                                            const pct = total ? Math.round((count / total) * 100) : 0;
                                            return (
                                                <li key={path} className="analytics-top-row">
                                                    <span className="analytics-top-path">{formatPath(path)}</span>
                                                    <span className="analytics-top-bar-track">
                                                        <span className="analytics-top-bar-fill" style={{ width: `${pct}%` }} />
                                                    </span>
                                                    <span className="analytics-top-count">{count}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>

                            <div className="analytics-card">
                                <h3 className="analytics-card-heading"><LuGlobe /> Top locations</h3>
                                {locations.length === 0 ? (
                                    <p className="analytics-empty">No location data yet.</p>
                                ) : (
                                    <ul className="analytics-top-list">
                                        {locations.map(({ location, count }) => {
                                            const pct = uniqueVisitorCount ? Math.round((count / uniqueVisitorCount) * 100) : 0;
                                            return (
                                                <li key={location} className="analytics-top-row">
                                                    <span className="analytics-top-path">{location}</span>
                                                    <span className="analytics-top-bar-track">
                                                        <span className="analytics-top-bar-fill" style={{ width: `${pct}%` }} />
                                                    </span>
                                                    <span className="analytics-top-count">{count}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>

                            <div className="analytics-card">
                                <h3 className="analytics-card-heading"><LuMonitor /> Devices</h3>
                                {devices.length === 0 ? (
                                    <p className="analytics-empty">No device data yet.</p>
                                ) : (
                                    <ul className="analytics-top-list">
                                        {devices.map(({ type, count }) => {
                                            const pct = uniqueVisitorCount ? Math.round((count / uniqueVisitorCount) * 100) : 0;
                                            const Icon = DEVICE_ICONS[type] || LuMonitor;
                                            return (
                                                <li key={type} className="analytics-top-row">
                                                    <span className="analytics-top-path analytics-top-path-icon"><Icon className="analytics-top-icon" aria-hidden="true" /> {type}</span>
                                                    <span className="analytics-top-bar-track">
                                                        <span className="analytics-top-bar-fill" style={{ width: `${pct}%` }} />
                                                    </span>
                                                    <span className="analytics-top-count">{count}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        </div>

                        <div className="analytics-card analytics-logs-card">
                            <h3 className="analytics-card-heading"><LuHistory /> Recent visits</h3>
                            <p className="analytics-log-hint">Grouped by device - click any row to see its full visit history.</p>
                            {groups.length === 0 ? (
                                <p className="analytics-empty">No visits logged yet.</p>
                            ) : (
                                <div className="analytics-log-list">
                                    {groups.map((group) => {
                                        const geo = group.geo;
                                        const device = group.device;
                                        const DeviceIcon = (device && DEVICE_ICONS[device.deviceType]) || LuMonitor;
                                        const deviceChip = formatDeviceChip(device);
                                        return (
                                            <div
                                                className="analytics-log-row analytics-log-row-clickable"
                                                key={group.key}
                                                onClick={() => this.openGroupModal(group.key)}
                                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), this.openGroupModal(group.key))}
                                                role="button"
                                                tabIndex={0}
                                                aria-label={`View ${group.count} visit${group.count === 1 ? '' : 's'} from ${formatLocation(geo)}`}
                                            >
                                                <span className="analytics-log-path">{formatPath(group.lastVisit.path)}</span>
                                                <span className="analytics-log-location">
                                                    {geo && geo.country_code && (
                                                        <span className="analytics-log-flag" aria-hidden="true">{countryFlagEmoji(geo.country_code)}</span>
                                                    )}
                                                    {formatLocation(geo)}
                                                </span>
                                                {geo && geo.ip && <span className="analytics-log-ip">{geo.ip}</span>}
                                                {device && (
                                                    <span className="analytics-log-device">
                                                        <DeviceIcon className="analytics-log-device-icon" aria-hidden="true" />
                                                        {deviceChip || device.deviceType}
                                                        {device.screenWidth && device.screenHeight && (
                                                            <span className="analytics-log-screen">{device.screenWidth}×{device.screenHeight}</span>
                                                        )}
                                                    </span>
                                                )}
                                                <span className="analytics-log-time">{new Date(group.lastVisit.timestamp).toLocaleString()}</span>
                                                {group.count > 1 && <span className="analytics-log-count-badge">{group.count} visits</span>}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {selectedGroup && (
                            <div
                                className={`device-log-modal-backdrop${this.state.modalClosing ? ' is-closing' : ''}`}
                                onClick={this.closeGroupModal}
                            >
                                <div className="device-log-modal-panel" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        type="button"
                                        className="device-log-modal-close"
                                        onClick={this.closeGroupModal}
                                        aria-label="Close"
                                    >
                                        <LuX />
                                    </button>
                                    <span className="device-log-modal-eyebrow"><LuHistory /> Visit history</span>
                                    <h3 className="device-log-modal-heading">
                                        {selectedGeo && selectedGeo.country_code && (
                                            <span aria-hidden="true">{countryFlagEmoji(selectedGeo.country_code)} </span>
                                        )}
                                        {formatLocation(selectedGeo)}
                                    </h3>
                                    <div className="device-log-modal-meta">
                                        {selectedGeo && selectedGeo.ip && <span className="analytics-log-ip">{selectedGeo.ip}</span>}
                                        {selectedDevice && (
                                            <span className="analytics-log-device">
                                                <SelectedDeviceIcon className="analytics-log-device-icon" aria-hidden="true" />
                                                {formatDeviceChip(selectedDevice) || selectedDevice.deviceType}
                                                {selectedDevice.screenWidth && selectedDevice.screenHeight && (
                                                    <span className="analytics-log-screen">{selectedDevice.screenWidth}×{selectedDevice.screenHeight}</span>
                                                )}
                                            </span>
                                        )}
                                        <span className="device-log-modal-count">{selectedGroup.count} visit{selectedGroup.count === 1 ? '' : 's'} total</span>
                                    </div>
                                    <div className="device-log-modal-list">
                                        {selectedGroup.entries.map((entry, i) => (
                                            <div className="device-log-modal-row" key={`${entry.timestamp}-${i}`}>
                                                <span className="device-log-modal-row-path">{formatPath(entry.path)}</span>
                                                <span className="device-log-modal-row-time">{new Date(entry.timestamp).toLocaleString()}</span>
                                                {entry.referrer && entry.referrer !== 'direct' && (
                                                    <span className="analytics-log-referrer" title={entry.referrer}>
                                                        via {entry.referrer.replace(/^https?:\/\//, '')}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {this.state.statModalKey && (
                            <div
                                className={`device-log-modal-backdrop${this.state.statModalClosing ? ' is-closing' : ''}`}
                                onClick={this.closeStatModal}
                            >
                                <div className="device-log-modal-panel" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        type="button"
                                        className="device-log-modal-close"
                                        onClick={this.closeStatModal}
                                        aria-label="Close"
                                    >
                                        <LuX />
                                    </button>
                                    {this.state.statModalKey === 'countries' ? (
                                        <>
                                            <span className="device-log-modal-eyebrow"><LuGlobe /> Breakdown</span>
                                            <h3 className="device-log-modal-heading">Countries reached</h3>
                                            {countries.length === 0 ? (
                                                <p className="analytics-empty">No country data yet.</p>
                                            ) : (
                                                <div className="device-log-modal-list">
                                                    <ul className="analytics-top-list">
                                                        {countries.map(({ country, countryCode, count }) => {
                                                            const pct = uniqueVisitorCount ? Math.round((count / uniqueVisitorCount) * 100) : 0;
                                                            return (
                                                                <li key={country} className="analytics-top-row">
                                                                    <span className="analytics-top-path analytics-top-path-icon">
                                                                        {countryCode && <span aria-hidden="true">{countryFlagEmoji(countryCode)}</span>}
                                                                        {country}
                                                                    </span>
                                                                    <span className="analytics-top-bar-track">
                                                                        <span className="analytics-top-bar-fill" style={{ width: `${pct}%` }} />
                                                                    </span>
                                                                    <span className="analytics-top-count">{count}</span>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <span className="device-log-modal-eyebrow"><LuMapPin /> Breakdown</span>
                                            <h3 className="device-log-modal-heading">States reached</h3>
                                            {regions.length === 0 ? (
                                                <p className="analytics-empty">No state/region data yet.</p>
                                            ) : (
                                                <div className="device-log-modal-list">
                                                    <ul className="analytics-top-list">
                                                        {regions.map(({ key, label, count }) => {
                                                            const pct = uniqueVisitorCount ? Math.round((count / uniqueVisitorCount) * 100) : 0;
                                                            return (
                                                                <li key={key} className="analytics-top-row">
                                                                    <span className="analytics-top-path">{label}</span>
                                                                    <span className="analytics-top-bar-track">
                                                                        <span className="analytics-top-bar-fill" style={{ width: `${pct}%` }} />
                                                                    </span>
                                                                    <span className="analytics-top-count">{count}</span>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                        </>
                    )}
                </div>
            </div>
        );
    }
}
